// apps/web/src/pages/author/widget-config/size-and-yield-config.tsx
//
// Phase 8 config sub-form for template `size-and-yield` — drives the inline
// configSchema editor per authr-05. Field set mirrors
// packages/shared/src/schemas/widget-templates/size-and-yield.ts AND the
// per-template walker at packages/shared/src/forms/per-template/size-and-yield.ts
// (Plan 03).
//
// Sub-form fields use per-path useWatch only — NEVER top-level (RESEARCH
// §"Pattern 3"). No formState reads. Every useFieldArray map uses field.id
// keys per authr-03 (Plan 01 grep gate enforces).
//
// configSchema shape (verbatim):
//   steps: ReadonlyArray<{ id; label; multiplier; min?; max? }>     (min 1, max 20)
//   yieldDisplay: { perStepFormula; unitsField; baseValue }
//   defaultStepId?: string
//
// W11 — exports `defaultSizeAndYieldConfig(): SizeAndYieldConfig` factory.

import type { SizeAndYieldConfig } from "@the-recipe-spread/shared";
import { useFieldArray, useFormContext } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FieldArrayRow } from "../FieldArrayRow";

export interface SizeAndYieldConfigSubFormProps {
  instanceId: string;
  basePath: string;
}

// W11 — minimum-valid SizeAndYieldConfig per the configSchema:
//   steps.min(1), yieldDisplay required, defaultStepId optional.
export function defaultSizeAndYieldConfig(): SizeAndYieldConfig {
  return {
    steps: [{ id: "step-1", label: "1x", multiplier: 1 }],
    yieldDisplay: {
      perStepFormula: "{value} {units}",
      unitsField: "servings",
      baseValue: 1,
    },
  };
}

export function SizeAndYieldConfigSubForm({
  instanceId: _instanceId,
  basePath,
}: SizeAndYieldConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `${basePath}.steps`,
  });

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="STEPS">
        {fields.length === 0 ? (
          <EmptyState
            title="No steps yet."
            cta={{
              label: "Add step",
              onClick: () =>
                append({
                  id: `step-${fields.length + 1}`,
                  label: `${fields.length + 1}x`,
                  multiplier: 1,
                }),
            }}
          />
        ) : (
          <>
            {fields.map((field, index) => (
              <FieldArrayRow
                key={field.id}
                singular="step"
                index={index}
                canMoveUp={index > 0}
                canMoveDown={index < fields.length - 1}
                onMoveUp={() => move(index, index - 1)}
                onMoveDown={() => move(index, index + 1)}
                onRemove={() => remove(index)}
                deleteDialogTitle="Delete step?"
                deleteDialogBody="This step will be removed from the size selector."
                deleteConfirmLabel="Delete step"
              >
                <FormField
                  control={control}
                  name={`${basePath}.steps.${index}.id`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.steps.${index}.id`}>
                      <FormLabel>Id (kebab-case)</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.steps.${index}.label`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.steps.${index}.label`}>
                      <FormLabel>Label</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.steps.${index}.multiplier`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.steps.${index}.multiplier`}>
                      <FormLabel>Multiplier</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          value={f.value ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            f.onChange(raw === "" ? undefined : Number(raw));
                          }}
                          onBlur={f.onBlur}
                          name={f.name}
                          ref={f.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FieldArrayRow>
            ))}
            <button
              type="button"
              onClick={() =>
                append({
                  id: `step-${fields.length + 1}`,
                  label: `${fields.length + 1}x`,
                  multiplier: 1,
                })
              }
              className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              Add step
            </button>
          </>
        )}
      </Fieldset>

      <Fieldset legend="YIELD DISPLAY">
        <FormField
          control={control}
          name={`${basePath}.yieldDisplay.baseValue`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.yieldDisplay.baseValue`}>
              <FormLabel>Base value (yield)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  value={f.value ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    f.onChange(raw === "" ? undefined : Number(raw));
                  }}
                  onBlur={f.onBlur}
                  name={f.name}
                  ref={f.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${basePath}.yieldDisplay.unitsField`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.yieldDisplay.unitsField`}>
              <FormLabel>Units</FormLabel>
              <FormControl>
                <Input {...f} value={f.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${basePath}.yieldDisplay.perStepFormula`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.yieldDisplay.perStepFormula`}>
              <FormLabel>Per-step formula (template string)</FormLabel>
              <FormControl>
                <Input {...f} value={f.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${basePath}.defaultStepId`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.defaultStepId`}>
              <FormLabel>Default step id (optional)</FormLabel>
              <FormControl>
                <Input {...f} value={f.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>
    </div>
  );
}
SizeAndYieldConfigSubForm.displayName = "SizeAndYieldConfigSubForm";
